<script setup lang="ts">
import { ref, watchEffect } from 'vue';
import type { Member } from '@ilteducation/content-collections-types';
import useLocale from '../helpers/use-locale';
import { MemberType } from '../types/share';
import ConfirmationDialog from '../ConfirmationDialog.vue';
import DialogDrawer from '../DialogDrawer.vue';
import ParticipantsSelection from '../ParticipantsSelection.vue';
import { useDisplay } from 'vuetify';

type Emits = {
  (e: 'close'): void;
  (e: 'updateParticipants', participants: Member[]): void;
  (e: 'update:modelValue', memberUserIds: string[]): void;
};

const emit = defineEmits<Emits>();

type Props = {
  modelValue: boolean; // state of dialog window
  shareName: string;
  isSharing: boolean;
  memberUserIds: string[];
  memberGroupIds: string[];
  dialogTitle?: string;
  canShareWithGroup?: boolean;
  userId?: string;
  userType?: 'employee' | 'student';
  size?: string;
  color?: string;
};

const props = withDefaults(defineProps<Props>(), {
  size: 'large',
  color: 'primary',
  dialogTitle: 'components.label_add_members',
  canShareWithGroup: true,
  userId: undefined,
  userType: undefined,
});
const { t } = useLocale();
const { smAndDown } = useDisplay();

const showDialog = ref<boolean>(false);
const exitDialog = ref<boolean>(false);
const selectedUserIds = ref<string[]>(props.memberUserIds);
const selectedGroupIds = ref<string[]>(props.memberGroupIds);

const exitConfirmation = () => {
  showDialog.value = false;
  exitDialog.value = false;
  emit('close');
};

const updateParticipants = (participants: Member[]) => {
  emit('updateParticipants', participants);
};

const share = () => {
  const selectedUserIdsValues = selectedUserIds.value.map((user) => user);
  const selectedGroupIdsValues = selectedGroupIds.value.map((group) => group);
  const hasNoChange =
    selectedUserIdsValues.length === props.memberUserIds.length &&
    selectedUserIdsValues.every((element, index) => element === props.memberUserIds[index]) &&
    selectedGroupIdsValues.length === props.memberGroupIds.length &&
    selectedGroupIdsValues.every((element, index) => element === props.memberGroupIds[index]);

  if (!hasNoChange) {
    if (selectedUserIds.value.length === 0 && selectedGroupIds.value.length === 0) {
      updateParticipants([]);
      return;
    }
    const members: Member[] = [];
    selectedUserIds.value.forEach((user) => {
      if (members.find((member: Member) => member.id === user)) {
        return;
      }
      members.push({
        type: MemberType.User,
        id: user,
      });
    });
    selectedGroupIds.value.forEach((group) => {
      if (members.find((member: Member) => member.id === group)) {
        return;
      }
      members.push({
        type: MemberType.Group,
        id: group,
      });
    });
    updateParticipants(members);
  } else {
    emit('close');
  }
};

const unshare = () => {
  selectedUserIds.value = props.memberUserIds;
  selectedGroupIds.value = props.memberGroupIds;
  emit('update:modelValue', props.memberUserIds);
  emit('update:modelValue', props.memberGroupIds);
};

watchEffect(() => {
  selectedUserIds.value = props.memberUserIds;
  selectedGroupIds.value = props.memberGroupIds;
});

const cancel = () => {
  selectedUserIds.value = props.memberUserIds;
  selectedGroupIds.value = props.memberGroupIds;
  emit('close');
};

// TODO implement stop sharing
// const removeShare = () => {};
</script>

<template>
  <DialogDrawer
    class="mt-4"
    :class="smAndDown ? 'mx-0' : 'mx-4'"
    :model-value="modelValue"
    :title-text="t(dialogTitle)"
    :max-height="'97%'"
    :max-width="'80%'"
    :full-height="true"
    :has-done-button-to-share="true"
    :has-cancel-button="true"
    @share="share"
    @unshare="unshare"
    @close="emit('close')"
    @cancel="cancel"
  >
    <template #drawerTopbar>
      <v-card-title ref="filterHeaderRef" class="d-flex justify-space-between align-center px-0">
        <v-btn variant="text" rounded class="add-dialog-close" @click="cancel">
          <span class="text-none">{{ t('components.cancel') }}</span>
        </v-btn>
        <div class="text-h5">
          {{ t(dialogTitle) }}
        </div>
        <v-btn variant="text" rounded class="add-dialog-close" @click="share">
          <span class="text-none">{{ t('components.button_done') }}</span>
        </v-btn>
      </v-card-title>
    </template>
    <v-overlay :model-value="isSharing" contained class="align-center justify-center">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-overlay>
    <ParticipantsSelection
      :pre-selected-user-ids="selectedUserIds"
      :pre-selected-group-ids="selectedGroupIds"
      :can-share-with-group="canShareWithGroup"
      :user-type="userType"
      :user-id="userId"
      @update:user="selectedUserIds = $event"
      @update:group="selectedGroupIds = $event"
    />
  </DialogDrawer>
  <ConfirmationDialog
    v-model="exitDialog"
    :no-btn-label="t('components.no')"
    title="Cancel sharing"
    :text="t('components.cancel_dialog')"
    :yes-btn-label="t('components.yes')"
    @dialog-confirmation="exitConfirmation"
    @close-dialog="exitDialog = false"
  />
</template>

<style scoped lang="scss"></style>
